import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const navigate = useNavigate();
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const tokenMissing = !token || token.length < 20;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pwd.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (pwd !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      toast.success("Password updated. Sign in with your new password.");
      navigate("/login", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[80vh] grid lg:grid-cols-2 bg-white" data-testid="reset-password-page">
      <div className="hidden lg:block relative">
        <img src="/hero-model.jpg" alt="" className="absolute inset-0 w-full h-full object-cover object-[center_20%]" />
        <div className="absolute inset-0 bg-gradient-to-tr from-black via-black/40 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-12 text-white">
          <h2 className="font-display text-7xl tracking-tighter leading-[0.85]">NEW<br />PASSWORD,<br /><span className="text-[#FF3B30]">LOCKED IN.</span></h2>
          <p className="text-white/80 mt-6 max-w-md">Pick something strong — minimum 6 characters. Mix letters, numbers, and a symbol if you can.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-8 bg-white">
        <form onSubmit={submit} className="w-full max-w-md space-y-6">
          <div>
            <div className="font-mono text-xs text-[#FF3B30] tracking-widest">ELMNTS / RECOVERY</div>
            <h1 className="font-display text-5xl tracking-tighter mt-2 text-black">SET A NEW PASSWORD</h1>
          </div>

          {tokenMissing ? (
            <div className="space-y-4">
              <div className="bg-[#FF3B30]/10 border border-[#FF3B30]/30 p-5 text-sm text-black/80" data-testid="reset-missing-token">
                This reset link is missing or invalid. Request a new one to continue.
              </div>
              <Link to="/forgot-password" className="block text-center bg-black text-white py-4 font-display tracking-widest text-sm hover:bg-[#FF3B30] transition-colors" data-testid="link-request-new">
                REQUEST A NEW LINK
              </Link>
            </div>
          ) : (
            <>
              <label className="block">
                <span className="font-display tracking-widest text-[10px] text-black/50">NEW PASSWORD</span>
                <input
                  type="password"
                  required
                  value={pwd}
                  minLength={6}
                  onChange={(e) => setPwd(e.target.value)}
                  className="w-full bg-transparent border-b border-black/30 focus:border-black outline-none py-3 text-black"
                  data-testid="reset-password"
                />
              </label>
              <label className="block">
                <span className="font-display tracking-widest text-[10px] text-black/50">CONFIRM PASSWORD</span>
                <input
                  type="password"
                  required
                  value={confirm}
                  minLength={6}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full bg-transparent border-b border-black/30 focus:border-black outline-none py-3 text-black"
                  data-testid="reset-confirm"
                />
              </label>
              {error && <p className="text-sm text-[#FF3B30]" data-testid="reset-error">{error}</p>}
              <button
                type="submit"
                disabled={busy}
                className="w-full bg-black text-white py-4 font-display tracking-widest text-sm hover:bg-[#FF3B30] transition-colors disabled:opacity-60"
                data-testid="reset-submit"
              >
                {busy ? "UPDATING..." : "UPDATE PASSWORD"}
              </button>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
